/**
 * Switch — an immediate-effect toggle (never a form value you have to save).
 *
 * The knob deliberately inverts when on: `--text-inverse` on `--focus` is the
 * same contrast pair `Checkbox` uses (6.46:1 dark, 8.00:1 light), so the "on"
 * state reads at a glance in both themes instead of relying on a colour cue
 * alone. It is `--focus` and not `--accent` because gold is spent on create and
 * nothing else — see `Checkbox`, which carries the measurement.
 */

import { forwardRef, useId } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import * as RSwitch from '@radix-ui/react-switch';
import { cn } from '@/lib/cn';

type SwitchSize = 'sm' | 'md' | 'lg';

const TRACK: Record<SwitchSize, string> = {
  sm: 'h-3.5 w-6',
  md: 'h-4 w-7',
  lg: 'h-5 w-[34px]',
};

const THUMB: Record<SwitchSize, string> = {
  sm: 'size-2.5 data-[state=checked]:translate-x-2.5',
  md: 'size-3 data-[state=checked]:translate-x-3',
  lg: 'size-4 data-[state=checked]:translate-x-3.5',
};

export interface SwitchProps
  extends Omit<ComponentPropsWithoutRef<typeof RSwitch.Root>, 'children'> {
  label?: ReactNode;
  description?: ReactNode;
  size?: SwitchSize;
  containerClassName?: string;
  /**
   * Overrides the label's type step. A toggle sitting inside dense chrome — a
   * toolbar, a menu — has to rank *below* the thing it modifies; the default
   * step is the settings-page one, where the label is the row.
   */
  labelClassName?: string;
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  {
    label,
    description,
    size = 'md',
    className,
    containerClassName,
    labelClassName,
    disabled,
    id,
    ...rest
  },
  ref,
) {
  const generated = useId();
  const switchId = id ?? generated;

  const control = (
    <RSwitch.Root
      ref={ref}
      id={switchId}
      disabled={disabled}
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full border p-px',
        /* WCAG 2.2 SC 2.5.8 — the track is 14px tall by design, so the *target*
           is a transparent box centred on it, never the painted pill. Every
           pointer gets it, which is why this is here rather than `af-tap`: that
           utility is scoped to coarse pointers, and a 14px track is under the
           floor for a mouse as well. */
        'before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
        'before:h-[var(--h-control-sm)] before:w-[var(--h-control-sm)] before:min-w-full before:content-[""]',
        'transition-[background-color,border-color] duration-[var(--dur-fast)] ease-[var(--ease-out)]',
        TRACK[size],
        'border-line-strong bg-surface-inset',
        // See Checkbox: gold is spent on create and nothing else, so the "on"
        // state of a control is `--focus` — the token that already means "the
        // thing the reader is on". It pays its own 3:1 boundary in both themes
        // (6.46:1 dark, 8.00:1 light on `--surface-1`), so there is no separate
        // edge step. The twins wear one treatment.
        'data-[state=checked]:border-focus data-[state=checked]:bg-focus',
        !disabled && 'hover:border-focus',
        'disabled:cursor-not-allowed disabled:border-line-subtle disabled:bg-surface-3',
        'disabled:data-[state=checked]:border-line-subtle disabled:data-[state=checked]:bg-surface-3',
        className,
      )}
      style={{ borderRadius: 'var(--radius-full)' }}
      {...rest}
    >
      <RSwitch.Thumb
        className={cn(
          'block rounded-full bg-muted shadow-1',
          'transition-[transform,background-color] duration-[var(--dur-fast)] ease-[var(--ease-out)]',
          THUMB[size],
          // The ink of the inverted ground, which is what the checked track now
          // is: 6.46:1 on the dark fill, 8.00:1 on the light one.
          'data-[state=checked]:bg-inverse',
        )}
      />
    </RSwitch.Root>
  );

  if (!label && !description) return control;

  return (
    <div className={cn('flex items-start gap-2.5', containerClassName)}>
      {control}
      <div className="min-w-0">
        {label ? (
          <label
            htmlFor={switchId}
            className={cn(
              'block text-base leading-[var(--leading-snug)] select-none',
              disabled ? 'cursor-not-allowed text-muted' : 'cursor-pointer text-primary',
              labelClassName,
            )}
          >
            {label}
          </label>
        ) : null}
        {description ? (
          <p className="mt-1 text-xs text-muted leading-snug">{description}</p>
        ) : null}
      </div>
    </div>
  );
});
